import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import MarkEmailRead from "@mui/icons-material/MarkEmailRead";
import MarkEmailUnread from "@mui/icons-material/MarkEmailUnread";
import PublicIcon from "@mui/icons-material/Public";
import SaveIcon from "@mui/icons-material/Save";
import UndoIcon from "@mui/icons-material/Undo";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { green, lightBlue } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import parse from "html-react-parser";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import {
  getIndividualByUserId,
  populateIndividualsByUserIds,
} from "marketplace/individuals/_stores/individuals-redux-slice";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import MessagingAPIService from "messaging/_api/messaging-api-service";
import React, { useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import { addComment, removeComment } from "work/proposals/comments/_store/comments-redux-slice";
import Comment from "work/proposals/comments/comment";

const SystemCommentContainer = styled("div")(({ theme }) => ({
  "@container (min-width: 800px)": {
    width: "75%",
  },
  "@container (min-width: 0px)": {
    width: "100%",
  },
  margin: theme.spacing(0, "auto", 4, "auto"),
  whiteSpace: "normal",
  width: "80%",
}));
const SystemCommentInfo = styled("div")(({ theme }) => ({
  alignItems: "start",
  display: "flex",
  justifyContent: "space-between",
  marginBottom: theme.spacing(0.5),
}));
const SystemCommentTime = styled(Typography)(({ theme }) => ({
  fontSize: "0.8em",
  marginLeft: theme.spacing(1.32),
}));
const EditCommentInput = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: theme.palette.background.paper,
    "& fieldset": {
      borderColor: theme.palette.common.black,
    },
    "&:hover fieldset": {
      borderColor: theme.palette.common.black,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.common.black,
    },
  },
}));
const CommentListItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== "pending" && prop !== "unread",
})<{ pending: boolean; unread: boolean }>(({ pending, unread }) => ({
  opacity: pending ? 0.66 : 1,

  "& .MuiTypography-root": {
    fontWeight: unread ? 500 : "normal",
  },
}));
const NameLink = styled(Link, {
  shouldForwardProp: (prop) => prop !== "unread" && prop !== "disabled",
})<{ disabled: boolean; unread: boolean }>(({ theme, disabled, unread }) => ({
  display: "inline-block",
  cursor: disabled ? "default" : "pointer",
  textDecoration: disabled ? "none" : "underline",
  "&::before": {
    backgroundColor: theme.palette.secondary.main,
    borderRadius: "50%",
    content: '""',
    display: "inline-block",
    height: unread ? "8px" : 0,
    marginRight: unread ? "8px" : 0,
    verticalAlign: "middle",
    width: unread ? "8px" : 0,
  },
}));
const CommentDate = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  display: "block",
}));
const ExternalChip = styled(Chip)(({ theme }) => ({
  border: 0,
  marginLeft: theme.spacing(0.5),
}));
const PendingChip = styled(Chip)(() => ({
  border: 0,
}));
const CommentContent = styled("span")(({ theme }) => ({
  whiteSpace: "normal",
  width: "100%",
  "& span.action": {
    fontWeight: 500,
    "&.accepted": {
      color: theme.palette.success.main,
    },
    "&.rejected": {
      color: theme.palette.error.main,
    },
  },
  "& > span.comment-redline": {
    width: "100%",
    "& > span.change-group": {
      whiteSpace: "nowrap",
      "& .added": {
        "&.resolved": {
          color: theme.palette.text.primary,
        },
        backgroundColor: "unset",
        color: theme.palette.text.primary,
      },
    },
    "& span.added": {
      backgroundColor: green[300],
      color: theme.palette.text.primary,
      "&.resolved": {
        backgroundColor: "unset",
        color: lightBlue[700],
      },
    },
    "& span.removed": {
      textDecoration: "line-through",
      textDecorationColor: theme.palette.error.main,
      "&.resolved": {
        textDecorationColor: lightBlue[700],
      },
    },
    "& span.unchanged": {
      color: theme.palette.text.primary,
    },
  },
}));
const SystemMessageContent = styled("div")(({ theme }) => ({
  backgroundColor: "#e0e0e0",
  borderRadius: theme.spacing(0.66),
  display: "block",
  textAlign: "center",
  padding: theme.spacing(2),
  "& > p": {
    margin: 0,
  },
}));

type CommentItemProps = {
  className?: string;
  comment: Comment;
  audienceSwitch: React.ReactNode;
};

export default function CommentItem(props: Readonly<CommentItemProps>) {
  const { className, comment, audienceSwitch } = props;

  const [isBeingEdited, setIsBeingEdited] = React.useState<boolean>(false);
  const [editedCommentText, setEditedCommentText] = React.useState<string>("");
  const commenter = useSelector(getIndividualByUserId(comment.senderId ?? undefined));

  const { openDialog } = useDialog();
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();

  useEffect(() => {
    if (!commenter && comment.senderId) {
      dispatch(populateIndividualsByUserIds({ session, userIds: [comment.senderId] }));
    }
  }, [commenter, comment.senderId]);

  function getCommentContent(comment: Comment) {
    if (comment.isDeleted && !comment.markedForDeletion) {
      return <i>This comment was deleted</i>;
    }
    return <CommentContent>{comment.content ? parse(comment.content) : null}</CommentContent>;
  }

  function handleViewCommenterProfile() {
    if (!commenter) {
      console.warn("Commenter not found");
      return;
    }

    openDialog({
      component: <ViewIndividualProfile profile={commenter.profile} />,
      titleStyle: {
        paddingBottom: 0
      },
      contentSxProps: {
        display: "flex",
        overflowX: "hidden",
      },
      MuiProps: {
        maxWidth: "lg",
        fullWidth: true,
      },
    });
    setTimeout(() => adjustDialogPosition(false), 500);
  }

  function renderReadUnreadButton(comment: Comment): React.ReactNode {
    return (
      <Tooltip
        title={
          (!comment.markedForReadReceipt && !comment.isReadByUser(session.user?.id)) || comment.markedForUnread
            ? "Mark Read"
            : "Mark Unread"
        }
      >
        <IconButton
          color="primary"
          size="small"
          style={{ alignContent: "right", float: "right" }}
          onClick={async () => {
            const apiService = new MessagingAPIService(session);
            if (!comment.id) return;
            if (comment.isReadByUser(session.user?.id)) {
              comment = comment.markForUnread();
            } else {
              comment = comment.markForReadReceipt();
            }
            const returnedMessages = await apiService.createBulkMessages([
              comment.toMessage({
                isViewingAsVendor: session.context?.viewingAsVendor ?? false,
              }),
            ]);
            dispatch(addComment(Comment.fromMessage(returnedMessages[0])));
          }}
        >
          {(!comment.markedForReadReceipt && !comment.isReadByUser(session.user?.id)) || comment.markedForUnread ? (
            <MarkEmailUnread fontSize="inherit" />
          ) : (
            <MarkEmailRead fontSize="inherit" />
          )}
        </IconButton>
      </Tooltip>
    );
  }

  return comment.senderId ? (
    <CommentListItem
      className={className}
      key={comment.id?.value ?? Guid.generate().value}
      pending={comment.isPending}
      unread={Boolean(
        (comment.senderId &&
          !comment.senderId.isEqualTo(session.user?.id) &&
          !comment.markedForReadReceipt &&
          !comment.isReadByUser(session.user?.id)) ||
          comment.markedForUnread
      )}
      disableGutters
      alignItems="flex-start"
    >
      <ListItemAvatar>
        <IndividualAvatar individual={commenter ?? undefined} session={session} />
      </ListItemAvatar>
      {isBeingEdited && (
        <EditCommentInput
          multiline
          rows={4}
          variant="outlined"
          fullWidth
          value={editedCommentText}
          helperText={audienceSwitch}
          slotProps={{
            input: {
              endAdornment: (
                <>
                  <Tooltip title="Save comment">
                    <IconButton
                      color="primary"
                      size="small"
                      disabled={editedCommentText.trim() === ""}
                      onClick={() => {
                        if (editedCommentText.trim() === "") return;
                        dispatch(addComment(comment.edit(editedCommentText)));
                        setIsBeingEdited(false);
                      }}
                    >
                      <SaveIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Cancel editing">
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => {
                        setIsBeingEdited(false);
                        setEditedCommentText("");
                      }}
                    >
                      <CancelIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </>
              ),
            },
          }}
          onKeyDown={(event) => {
            if (event.key === KeyboardKey.Enter && editedCommentText.trim() !== "") {
              event.preventDefault();
              let updatedComment = comment.edit(editedCommentText);
              updatedComment = updatedComment.markForEdit();
              dispatch(addComment(updatedComment));
            } else if (event.key === KeyboardKey.Escape) {
              setIsBeingEdited(false);
            }
          }}
          onChange={(event) => setEditedCommentText(event.target.value)}
        />
      )}
      {!isBeingEdited && comment.senderId && (
        <ListItemText
          primary={
            <>
              <NameLink
                disabled={!commenter?.isVisible}
                unread={Boolean(
                  (comment.senderId &&
                    !comment.senderId.isEqualTo(session.user?.id) &&
                    !comment.markedForReadReceipt &&
                    !comment.isReadByUser(session.user?.id)) ||
                    comment.markedForUnread
                )}
                onClick={(event) => {
                  event.preventDefault();
                  if (!commenter?.isVisible) return;
                  handleViewCommenterProfile();
                }}
              >
                {commenter?.fullName}
              </NameLink>
              {comment.thread.isExternal && (
                <ExternalChip label="External" size="small" icon={<PublicIcon />} variant="outlined" />
              )}
              {comment.isPending && (
                <PendingChip label={getPendingStatus(comment)} size="small" color="warning" variant="outlined" />
              )}
              {!comment.updated && (
                <CommentDate variant="caption">
                  {comment.published
                    ? "Published: " + comment.published?.format("MMM D, YYYY h:mm A")
                    : new AHDate().format("MMM D, YYYY h:mm A")}
                </CommentDate>
              )}
              {comment.updated && (
                <CommentDate variant="caption">Edited: {comment.updated.format("MMM D, YYYY h:mm A")}</CommentDate>
              )}
            </>
          }
          secondary={getCommentContent(comment)}
          slotProps={{
            primary: {
              fontWeight: 500,
            },
            secondary: {
              color: "textPrimary",
            },
          }}
        />
      )}
      {comment.senderId && !comment.senderId.isEqualTo(session.user?.id) && renderReadUnreadButton(comment)}
      {(!comment.isDeleted || comment.markedForDeletion) && comment.senderId?.isEqualTo(session.user?.id) && (
        <>
          {!isBeingEdited && !comment.isAutoGenerated && !comment.isDeleted && (
            <>
              <Tooltip title="Edit comment">
                <IconButton
                  color="primary"
                  size="small"
                  onClick={() => {
                    setIsBeingEdited(true);
                    setEditedCommentText(comment.content ?? "");
                  }}
                >
                  <EditIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete comment">
                <IconButton
                  color="primary"
                  size="small"
                  onClick={() => {
                    if (comment.markedForCreation) {
                      dispatch(removeComment(comment));
                      return;
                    }
                    let updatedComment = comment.setDeleted();
                    updatedComment = updatedComment.markForDeletion();
                    dispatch(addComment(updatedComment));
                  }}
                >
                  <DeleteIcon color="error" fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </>
          )}
          {comment.isPending && comment.isDeleted && (
            <Tooltip title="Undo deletion">
              <IconButton
                color="primary"
                size="small"
                onClick={() => {
                  if (!comment.id) return;
                  let updatedComment = comment.setUndeleted();
                  dispatch(addComment(updatedComment));
                }}
              >
                <UndoIcon color="secondary" fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </>
      )}
    </CommentListItem>
  ) : (
    <SystemCommentContainer>
      <SystemMessageContent>
        <ReactMarkdown>{comment.content}</ReactMarkdown>
      </SystemMessageContent>
      <SystemCommentInfo>
        <SystemCommentTime>
          <time>{comment.published?.format("MM/DD/YY hh:mm A")}</time>
        </SystemCommentTime>
        {renderReadUnreadButton(comment)}
      </SystemCommentInfo>
    </SystemCommentContainer>
  );
}

function getPendingStatus(comment: Comment): string {
  if (comment.isDeleted) return "Pending Deletion";
  if (comment.markedForEdit) return "Pending Edit";
  return "Pending";
}

function adjustDialogPosition(panelOpen: boolean) {
  for (const dialog of document.getElementsByClassName("MuiDialog-root")) {
    dialog.setAttribute("style", `padding-right: ${panelOpen ? "0px" : "400px"}; transition: padding-right 225ms;`);
  }
}
